import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { integrationIconByType } from '@/components/atoms/Icons/integrationIconByType';
import {
  PopoverComboboxContent,
  PopoverComboboxEmpty,
  PopoverComboboxGroup,
  PopoverComboboxInput,
  PopoverComboboxItem,
  PopoverComboboxList,
  PopoverComboboxOption,
} from '@/components/ui/custom/PopoverCombobox';
import { Input } from '@/components/ui/input';
import { PopoverContent } from '@/components/ui/popover';
import useBusinessHours from '@/hooks/useBusinessHours';
import { useDeskId } from '@/hooks/useDeskId';
import { useIntegrations } from '@/hooks/useIntegrations';
import useMembers from '@/hooks/useMembers';
import { selectAvailableChannels } from '@/redux/integrations/selectors';
import { languageCodes } from '@/shared/languageCodes';
import { countryListAllIsoData } from '@/util/countries';
import { timezoneMaker } from '@/util/geography';
import { timezones } from '@/util/timezones';
import { isKeyEnter } from '@/util/util';
import { isValidSlug } from '@/util/validation/patterns';

import { ConditionInputType, STATUS_LABEL_KEYS } from './constants';

const languageOptions = Object.entries(languageCodes).map(([value, label]) => ({ value, label }));
const countryOptions = countryListAllIsoData.map(({ code, name }) => ({
  value: code,
  label: name,
}));
const statusValues = Object.keys(STATUS_LABEL_KEYS);

const getTimezoneOptions = () =>
  timezoneMaker(timezones).sort((a, b) => a.label.localeCompare(b.label));

type ConditionValueEditorProps = {
  input: ConditionInputType;
  value: string;
  onChange: (value: string) => void;
  onDone: () => void;
};

type ValueEditorProps = Omit<ConditionValueEditorProps, 'input'>;

type ValueListProps = ValueEditorProps & { options: PopoverComboboxOption[] };

const ValueList = ({ options, value, onChange, onDone }: ValueListProps) => {
  const { t } = useTranslation();

  return (
    <PopoverComboboxContent className="w-64" commandProps={{ shouldFilter: true }}>
      <PopoverComboboxInput />
      <PopoverComboboxList>
        <PopoverComboboxEmpty>{t('common.no_results')}</PopoverComboboxEmpty>
        <PopoverComboboxGroup>
          {options.map((option) => (
            <PopoverComboboxItem
              key={option.value}
              value={option.label}
              selected={option.value === value}
              onSelect={() => {
                onChange(option.value);
                onDone();
              }}
            >
              {option.icon}
              {option.label}
            </PopoverComboboxItem>
          ))}
        </PopoverComboboxGroup>
      </PopoverComboboxList>
    </PopoverComboboxContent>
  );
};

const AgentList = (props: ValueEditorProps) => {
  const { members } = useMembers();
  const options = (members ?? []).map(({ user_id, name }) => ({ value: user_id, label: name }));
  return <ValueList options={options} {...props} />;
};

const IntegrationList = (props: ValueEditorProps) => {
  const deskId = useDeskId();
  const { integrations } = useIntegrations(deskId);
  const options = (integrations ?? []).map(({ integration_id, name, type }) => {
    const Icon = integrationIconByType[type];
    return { value: integration_id, label: name, icon: Icon ? <Icon size={16} /> : undefined };
  });
  return <ValueList options={options} {...props} />;
};

const BusinessHoursList = (props: ValueEditorProps) => {
  const { businessHours } = useBusinessHours();
  const options = (businessHours?.business_hours ?? []).map(({ business_hours_id, name }) => ({
    value: business_hours_id,
    label: name,
  }));
  return <ValueList options={options} {...props} />;
};

const ChannelList = (props: ValueEditorProps) => {
  const channels = useSelector(selectAvailableChannels);
  const options = channels.map((channel) => ({ value: channel, label: channel }));
  return <ValueList options={options} {...props} />;
};

const StatusList = (props: ValueEditorProps) => {
  const { t } = useTranslation();
  const options = statusValues.map((status) => ({
    value: status,
    label: t(STATUS_LABEL_KEYS[status]),
  }));
  return <ValueList options={options} {...props} />;
};

const TextValue = ({ value, onChange, onDone, isTag }: ValueEditorProps & { isTag: boolean }) => {
  const { t } = useTranslation();
  const isInvalid = isTag && Boolean(value) && !isValidSlug(value);

  return (
    <PopoverContent align="start" className="flex w-64 flex-col gap-1 p-3">
      <Input
        autoFocus
        value={value}
        placeholder={isTag ? t('rules.type_tag') : t('common.type_value')}
        aria-invalid={isInvalid}
        onChange={(e) => onChange(isTag ? e.target.value.trim() : e.target.value)}
        onKeyDown={(e) => {
          if (isKeyEnter(e)) {
            e.preventDefault();
            onDone();
          }
        }}
      />
      {isInvalid && <p className="text-sm text-warning">{t('rules.tags_rules')}</p>}
    </PopoverContent>
  );
};

export const ConditionValueEditor = ({ input, ...props }: ConditionValueEditorProps) => {
  switch (input) {
    case 'none':
      return null;
    case 'language':
      return <ValueList options={languageOptions} {...props} />;
    case 'country':
      return <ValueList options={countryOptions} {...props} />;
    case 'timezone':
      return <ValueList options={getTimezoneOptions()} {...props} />;
    case 'agent':
      return <AgentList {...props} />;
    case 'integration':
      return <IntegrationList {...props} />;
    case 'business_hours':
      return <BusinessHoursList {...props} />;
    case 'channel':
      return <ChannelList {...props} />;
    case 'status':
      return <StatusList {...props} />;
    default:
      return <TextValue isTag={input === 'tag'} {...props} />;
  }
};
